/**
 * Extracts plain text content from an HTML string, preserving line breaks.
 * Block-level elements (div, p, br, etc.) are converted to newline characters.
 *
 * @param html - The HTML string to parse
 * @returns Plain text with newlines for block-level elements
 *
 * @example
 * ```ts
 * const html = '<div>Hello<br/>World</div><p>New paragraph</p>';
 * const text = htmlToText(html);
 * // Returns: "Hello\nWorld\nNew paragraph"
 * ```
 */
export function htmlToText(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }

  // Create a temporary DOM element to parse the HTML
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Process the document body to extract text with newlines
  return extractTextWithNewlines(doc.body).trim();
}

/**
 * Recursively extracts text from a DOM node, inserting newlines for block-level elements.
 */
function extractTextWithNewlines(node: Node): string {
  let text = "";

  // Block-level elements that should create line breaks
  const blockElements = new Set([
    "ADDRESS",
    "ARTICLE",
    "ASIDE",
    "BLOCKQUOTE",
    "DETAILS",
    "DIALOG",
    "DD",
    "DIV",
    "DL",
    "DT",
    "FIELDSET",
    "FIGCAPTION",
    "FIGURE",
    "FOOTER",
    "FORM",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "HEADER",
    "HGROUP",
    "HR",
    "LI",
    "MAIN",
    "NAV",
    "OL",
    "P",
    "PRE",
    "SECTION",
    "TABLE",
    "UL",
    "TR",
    "TD",
    "TH",
  ]);

  // Self-closing elements that create line breaks
  const lineBreakElements = new Set(["BR", "HR"]);

  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      // Add text content, preserving whitespace within text nodes
      const textContent = child.textContent || "";
      text += textContent;
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as Element;
      const tagName = element.tagName;

      // Handle line break elements
      if (lineBreakElements.has(tagName)) {
        text += "\n";
        continue;
      }

      // Recursively process child elements
      const childText = extractTextWithNewlines(child);

      // Add newlines for block-level elements
      if (blockElements.has(tagName)) {
        // Add newline before if text doesn't already end with one
        if (text && !text.endsWith("\n")) {
          text += "\n";
        }
        text += childText;
        // Add newline after if child text doesn't already end with one
        if (childText && !childText.endsWith("\n")) {
          text += "\n";
        }
      } else {
        // Inline elements - just add the text
        text += childText;
      }
    }
  }

  return text;
}
